/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { useCallback, useEffect, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import NotificationIcon from '@atlaskit/icon/core/notification';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import rovoIcon from '../../../assets/rovo.png';

import messages from './messages';

export const ResearchNotificationPanel = ({
	researchCompleted,
	researchErrored,
	hasReceivedAnswerPart,
}: {
	researchCompleted: boolean | undefined;
	researchErrored: boolean | undefined;
	hasReceivedAnswerPart?: boolean;
}): false | JSX.Element => {
	const { formatMessage } = useIntl();
	const [notificationSupported, setNotificationSupported] = useState(true);
	const { sendAnalyticsEvent } = useAnalytics();

	const onNotificationRequestClicked = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'deepResearchNotifyWhenDoneButton',
		});
		try {
			// eslint-disable-next-line compat/compat
			Notification.requestPermission();
		} catch (error) {
			// Silently fail if notifications aren't supported
			setNotificationSupported(false);
		}
	}, [setNotificationSupported, sendAnalyticsEvent]);

	const fireNotification = useCallback(
		(title: string, body: string) => {
			try {
				sendAnalyticsEvent({
					action: 'fired',
					actionSubject: 'notification',
					actionSubjectId: 'deepResearchCompletedNotification',
				});
				// eslint-disable-next-line compat/compat
				const notifier = new Notification(title, {
					body,
					icon: rovoIcon,
				});
				notifier.onclick = () => {
					window.focus();
					notifier?.close();
				};
			} catch (error) {
				// Silently fail if notifications aren't supported
				setNotificationSupported(false);
			}
		},
		[sendAnalyticsEvent],
	);

	useEffect(() => {
		if (Notification.permission === 'granted' && researchCompleted) {
			if (researchCompleted) {
				const title = formatMessage(messages.researchCompletedNotificationTitle);
				const body = formatMessage(messages.researchCompletedNotificationBody);

				if (document.visibilityState !== 'visible') {
					try {
						fireNotification(title, body);
					} catch (error) {
						// Silently fail if notifications aren't supported
					}
				}
			}
		}
	}, [fireNotification, formatMessage, researchCompleted]);

	if (typeof Notification === 'undefined' || typeof Notification.permission === 'undefined') {
		return <></>;
	}

	// eslint-disable-next-line compat/compat
	const notificationPermission = Notification.permission;
	const isNotificationPermissionDenied = notificationPermission === 'denied';
	const isNotificationPermissionGranted = notificationPermission === 'granted';

	return (
		!researchCompleted &&
		!researchErrored &&
		!hasReceivedAnswerPart &&
		notificationSupported &&
		!isNotificationPermissionDenied && (
			<Stack space="space.150" alignInline="start">
				<Text size="medium" weight="regular">
					{formatMessage(messages.deepResearchNotificationPanelText)}
				</Text>
				{isNotificationPermissionGranted ? (
					<Inline space="space.100" alignBlock="center">
						<CheckCircleIcon label="" color={token('color.icon.success')} />
						<Text size="medium" weight="medium" color="color.text.subtle" maxLines={1}>
							{formatMessage(messages.deepResearchNotificationConfirmationLabelText)}
						</Text>
					</Inline>
				) : (
					<Button
						iconBefore={NotificationIcon}
						onClick={onNotificationRequestClicked}
						shouldFitContainer={false}
					>
						{formatMessage(messages.deepResearchNotificationButtonText)}
					</Button>
				)}
			</Stack>
		)
	);
};
